import React, { useEffect, useState } from 'react';
import { Cookie, Settings, X } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  getConsent,
  setConsent,
  ALL_ACCEPTED,
  ALL_REJECTED,
} from '../lib/cookieConsent';

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState(ALL_REJECTED);

  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      // First visit (or policy version changed) — show banner
      setVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    setConsent(ALL_ACCEPTED);
    setVisible(false);
    setPrefsOpen(false);
  };

  const handleRejectAll = () => {
    setConsent(ALL_REJECTED);
    setVisible(false);
    setPrefsOpen(false);
  };

  const handleSavePrefs = () => {
    setConsent(prefs);
    setVisible(false);
    setPrefsOpen(false);
  };

  const openPrefs = () => {
    // Seed switches with the current stored value (or all-rejected on first visit)
    setPrefs(getConsent() || ALL_REJECTED);
    setPrefsOpen(true);
  };

  if (!visible && !prefsOpen) return null;

  return (
    <>
      {/* Bottom banner */}
      {visible && (
        <div
          className="fixed bottom-0 left-0 right-0 z-[80] bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 shadow-lg"
          data-testid="cookie-banner"
          role="dialog"
          aria-live="polite"
        >
          <div className="max-w-6xl mx-auto px-4 py-4 sm:py-5 flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/40 flex-shrink-0">
                <Cookie className="w-5 h-5 text-teal-600 dark:text-teal-400" />
              </div>
              <div className="flex-1 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                <strong className="text-slate-900 dark:text-white">Usamos cookies para melhorar sua experiência.</strong>{' '}
                Cookies essenciais são necessários para o funcionamento do site. Cookies de análise nos ajudam a entender como você usa a plataforma.
                Você pode aceitar todos, recusar os não-essenciais ou personalizar suas preferências.{' '}
                <a
                  href="/politica-de-privacidade"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-600 dark:text-teal-400 hover:underline font-medium"
                  data-testid="cookie-banner-privacy-link"
                >
                  Saiba mais
                </a>.
              </div>
            </div>
            <div className="flex flex-wrap gap-2 lg:flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={openPrefs}
                data-testid="cookie-configure-button"
              >
                <Settings className="w-4 h-4 mr-1.5" />
                Configurar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRejectAll}
                data-testid="cookie-reject-button"
              >
                Apenas essenciais
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
                data-testid="cookie-accept-button"
                className="bg-teal-500 hover:bg-teal-600 text-white"
              >
                Aceitar todos
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences modal */}
      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="cookie-preferences-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cookie className="w-5 h-5 text-teal-600" />
              Preferências de Cookies
            </DialogTitle>
            <DialogDescription>
              Escolha quais tipos de cookies o Watizap pode usar enquanto você navega.
              Suas preferências ficam salvas no seu navegador.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Essential */}
            <div className="flex items-start justify-between gap-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800">
              <div className="flex-1">
                <h4 className="font-semibold text-sm">Essenciais</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Necessários para login, sessão e segurança. Não podem ser desativados.
                </p>
              </div>
              <Switch checked disabled data-testid="cookie-toggle-essential" />
            </div>

            {/* Analytics */}
            <div className="flex items-start justify-between gap-4 p-3 rounded-lg border">
              <div className="flex-1">
                <h4 className="font-semibold text-sm">Analytics</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Coleta dados anônimos de uso (PostHog) para nos ajudar a melhorar a plataforma.
                </p>
              </div>
              <Switch
                checked={prefs.analytics}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                data-testid="cookie-toggle-analytics"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0 pt-4">
            <Button
              variant="outline"
              onClick={handleRejectAll}
              data-testid="cookie-modal-reject-all"
            >
              Recusar todos
            </Button>
            <Button
              variant="outline"
              onClick={handleSavePrefs}
              data-testid="cookie-modal-save"
            >
              Salvar preferências
            </Button>
            <Button
              onClick={handleAcceptAll}
              data-testid="cookie-modal-accept-all"
              className="bg-teal-500 hover:bg-teal-600 text-white"
            >
              Aceitar todos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
